import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import { createElement } from 'react';
import { forwardRefWithAs } from '@stacks/ui-core';
import { Box } from '../box/index.esm.js';
import { Flex } from '../flex/index.esm.js';
import { transition } from '@stacks/ui-theme';
import { color } from '@stacks/ui-utils';
import { usePress } from 'react-aria';
import { Grid } from '../grid/index.esm.js';
import { Spinner } from '../spinner.esm.js';

var blue = function blue(alpha, darker) {
  if (alpha === void 0) {
    alpha = 1;
  }

  if (darker === void 0) {
    darker = false;
  }

  return "rgba(" + (darker ? "70,55,255" : "85,70,255") + "," + alpha + ")";
};
var focusBlue = function focusBlue(alpha) {
  if (alpha === void 0) {
    alpha = 1;
  }

  return "rgba(170, 179, 255," + alpha + ")";
};

var modeStyles = function modeStyles(mode, variant) {
  if (variant === void 0) {
    variant = "solid";
  }

  if (variant === "solid") {
    switch (mode) {
      case "secondary":
        return function (_ref) {
          var isPressed = _ref.isPressed,
              isDisabled = _ref.isDisabled,
              _hover = _ref._hover;
          return {
            bg: blue(0.2, isPressed),
            border: "1px solid",
            borderRadius: "6px",
            color: color("invert"),
            borderColor: blue(0),
            _hover: _extends({
              bg: blue(0.25, true),
              cursor: isDisabled ? "not-allowed" : "pointer"
            }, _hover),
            _focus: {
              boxShadow: "0 0 0 3px " + focusBlue(0.5)
            }
          };
        };

      case "tertiary":
        return function (_ref2) {
          var isPressed = _ref2.isPressed,
              isDisabled = _ref2.isDisabled,
              _hover = _ref2._hover;
          return {
            border: "1px solid",
            borderRadius: "6px",
            color: color("invert"),
            bg: color(isPressed ? "bg" : "bg-2"),
            borderColor: color("border"),
            boxShadow: "mid",
            _hover: _extends({
              bg: color("bg-3"),
              boxShadow: "high",
              cursor: isDisabled ? "not-allowed" : "pointer"
            }, _hover),
            _focus: {
              boxShadow: "0 0 0 3px " + focusBlue(0.5)
            }
          };
        };

      default:
        return function (_ref3) {
          var isPressed = _ref3.isPressed,
              isDisabled = _ref3.isDisabled,
              _hover = _ref3._hover;
          return {
            border: "1px solid",
            borderRadius: "6px",
            bg: blue(1, isPressed),
            color: "white",
            borderColor: blue(0),
            _hover: _extends({
              bg: blue(1, true),
              cursor: isDisabled ? "not-allowed" : "pointer"
            }, _hover),
            _focus: {
              boxShadow: "0 0 0 3px " + focusBlue(0.75)
            }
          };
        };
    }
  } else {
    return function (_ref4) {
      var _hover = _ref4._hover,
          isPressed = _ref4.isPressed,
          isDisabled = _ref4.isDisabled;
      return {
        color: blue(1, isPressed),
        textDecoration: "none",
        _hover: _extends({
          textDecoration: "underline",
          cursor: isDisabled ? "not-allowed" : "pointer"
        }, _hover)
      };
    };
  }
};

var sizeProps = function sizeProps(size) {
  switch (size) {
    case "sm":
      {
        return {
          px: "tight",
          py: "extra-tight",
          fontSize: "12px"
        };
      }

    case "md":
      {
        return {
          px: "base",
          py: "base-tight",
          fontSize: "14px"
        };
      }

    case "lg":
      {
        return {
          px: "base-loose",
          py: "base",
          fontSize: "14px"
        };
      }
  }
};

var Button = /*#__PURE__*/forwardRefWithAs(function (props, ref) {
  var children = props.children,
      _props$as = props.as,
      as = _props$as === void 0 ? "button" : _props$as,
      _onClick = props.onClick,
      _props$mode = props.mode,
      mode = _props$mode === void 0 ? "primary" : _props$mode,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? "solid" : _props$variant,
      isLoading = props.isLoading,
      isDisabled = props.isDisabled,
      _props$size = props.size,
      size = _props$size === void 0 ? "md" : _props$size,
      _hover = props._hover,
      rest = _objectWithoutPropertiesLoose(props, ["children", "as", "onClick", "mode", "variant", "isLoading", "isDisabled", "size", "_hover"]);

  var _usePress = usePress({
    ref: ref,
    onPress: function onPress(e) {
      _onClick == null ? void 0 : _onClick(e);
    }
  }),
      pressProps = _usePress.pressProps,
      isPressed = _usePress.isPressed;

  var onKeyUp = pressProps.onKeyUp,
      onKeyDown = pressProps.onKeyDown;
  var sizeStyles = variant === "link" ? {} : sizeProps(size);

  var _modeStyle = modeStyles(mode, variant);

  var modeStyle = _modeStyle({
    isPressed: isPressed,
    _hover: _hover,
    isDisabled: isDisabled
  });

  return /* @__PURE__ */createElement(Box, _extends({
    as: as,
    outline: "none",
    fontWeight: "500",
    transition: transition,
    userSelect: "none",
    ref: ref,
    onKeyUp: onKeyUp,
    onKeyDown: onKeyDown,
    position: "relative",
    pointerEvents: isLoading || isDisabled ? "none" : "unset",
    onClick: function onClick(e) {
      _onClick == null ? void 0 : _onClick(e);
    },
    opacity: isDisabled ? 0.4 : 1,
    alignItems: "center",
    justifyContent: "center"
  }, modeStyle, sizeStyles, rest, {
    display: rest.display || "inline-flex"
  }), /* @__PURE__ */createElement(Grid, {
    transition: transition,
    opacity: isLoading ? 1 : 0,
    zIndex: 2,
    position: "absolute",
    top: 0,
    left: 0,
    size: "100%",
    placeItems: "center"
  }, /* @__PURE__ */createElement(Spinner, {
    size: "sm",
    color: "currentColor"
  })), /* @__PURE__ */createElement(Flex, {
    transition: "opacity 0.2s cubic-bezier(0.23, 1, 0.32, 1)",
    as: "span",
    color: "currentColor",
    opacity: isLoading ? 0 : 1,
    display: rest.display || "inline-flex",
    alignItems: rest.alignItems || "center",
    justifyContent: rest.justifyContent || "center"
  }, children));
});

export { Button, blue, focusBlue };
//# sourceMappingURL=index.esm.js.map
